import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/api/query-keys";
import type { PaginatedResponse } from "@/api/types";
import { api } from "@/lib/api";
import type {
	ChartDataPoint,
	ChartParams,
	TableParams,
	WaterMeterDetail,
	WaterMeterRow,
} from "./types";
import { mapChartPoints } from "./utils";

export function useWaterChart(
	params: ChartParams,
): UseQueryResult<ChartDataPoint[], Error> {
	return useQuery({
		queryKey: queryKeys.districts.waterChart(params),
		queryFn: async (): Promise<ChartDataPoint[]> => {
			const res = await api.get<{ dt: string; sum: number }[]>(
				"/api/districts/water_chart/",
				{ params },
			);
			return mapChartPoints(res.data);
		},
	});
}

export function useWaterTable(
	params: TableParams,
): UseQueryResult<PaginatedResponse<WaterMeterRow>, Error> {
	return useQuery({
		queryKey: queryKeys.districts.waterTable(params),
		queryFn: async (): Promise<PaginatedResponse<WaterMeterRow>> => {
			const res = await api.get<PaginatedResponse<WaterMeterRow>>(
				"/api/districts/water_table/",
				{ params },
			);
			return res.data;
		},
	});
}

export function useWaterMeterDetail(
	serialNumber: string,
): UseQueryResult<WaterMeterDetail, Error> {
	return useQuery({
		queryKey: queryKeys.districts.waterMeterDetail(serialNumber),
		queryFn: async (): Promise<WaterMeterDetail> => {
			const res = await api.get<WaterMeterDetail>(
				`/api/districts/water_meter_detail/${serialNumber}/`,
			);
			return res.data;
		},
		enabled: !!serialNumber,
	});
}
